import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getOrder } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReorderWarningModal, {
    getReorderWarningMessage,
    hasNonReorderableItems,
} from "@WhiteCap/Components/ReorderWarningModal";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        order,
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,
};
interface OwnProps {
    orderNumber: string;
}
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;
    twoButtonModalStyles?: TwoButtonModalStyles;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
            button[data-test-selector^="popoverOverflowTrigger"] {
                width: 40px;
                background-color: transparent;
                border-color: transparent;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
            }
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "tertiary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
    twoButtonModalStyles: {
        submitButton: {
            color: "primary",
        },
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

const OrderDetailsKebabMenu: React.FC<Props> = ({
    orderNumber,
    reorder,
    showAddToCartConfirmationDialog,
    isReordering,
    isUpdatingCart,
    isVmiOrderDetailsPage,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [isValidating, setIsValidating] = useState(false);
    const [reorderWarningModalIsOpen, setReorderWarningModalIsOpen] = useState(false);
    const [reorderWarningMessage, setReorderWarningMessage] = useState<string | React.ReactNode>("");

    const onReorderSuccess = (orderNumber: string) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toasterContext.addToast({
            body: (
                <OrderDetailPageTypeLink
                    title={orderNumber}
                    orderNumber={orderNumber}
                    isVmiOrderDetailsPage={isVmiOrderDetailsPage}
                />
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };
    const onReorderError = (message: string) => {
        toasterContext.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    const handleReorder = () => {
        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber),
            onError: message => onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };

    const onClickReorder = async () => {
        setIsValidating(true);

        try {
            const orderData = await getOrder({
                orderNumber,
                expand: ["orderLines"],
            });

            if (orderData && hasNonReorderableItems(orderData)) {
                const message = getReorderWarningMessage(orderData);
                setReorderWarningMessage(message);
                setReorderWarningModalIsOpen(true);
                setIsValidating(false);
                return;
            }
            handleReorder();
        } catch (error) {
            toasterContext.addToast({
                body: "Unable to validate order items. Please try again.",
                messageType: "danger",
                timeoutLength: 6000,
            });
        } finally {
            setIsValidating(false);
        }
    };

    const handleWarningModalProceed = () => {
        setReorderWarningModalIsOpen(false);
        handleReorder();
    };

    return (
        <>
            <StyledWrapper {...styles.buttonsWrapper} className="kabab-menu">
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {!isReordering && !isValidating && (
                        <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isUpdatingCart}>
                            {translate("Reorder")}
                        </Clickable>
                    )}
                    {(isReordering || isValidating) && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                </OverflowMenu>
            </StyledWrapper>

            <ReorderWarningModal
                isOpen={reorderWarningModalIsOpen}
                message={reorderWarningMessage}
                onClose={() => setReorderWarningModalIsOpen(false)}
                onProceed={handleWarningModalProceed}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsKebabMenu));
